/**
 * Print-ready QR stand artwork builder (download-only).
 *
 * This module ONLY affects the downloaded PNG file. It never changes the
 * generated QR payload, the preview UI, or any API/business logic: it takes an
 * already generated QR data URL and composes it inside a branded 4in x 6in
 * (300 DPI) layout suitable for acrylic table stands / in-room displays.
 */

const CANVAS_WIDTH = 1200; // 4in @ 300dpi
const CANVAS_HEIGHT = 1800; // 6in @ 300dpi

const DISPLAY_FONT_STACK = '"Cormorant Garamond", "Playfair Display", Georgia, "Times New Roman", serif';
const TEXT_FONT_STACK = '"Jost", "Futura", "Avenir Next", "Helvetica Neue", Helvetica, sans-serif';

const PALETTE = {
  inkTop: '#0a1b26',
  inkBottom: '#10303a',
  accent: '#4ce1b6',
  accentSoft: 'rgba(76, 225, 182, 0.18)',
  // Matches the light colour baked into the generated QR image so the panel reads as one plane.
  paper: '#eef6ff',
  textPrimary: '#ffffff',
  textMuted: 'rgba(233, 246, 243, 0.62)',
};

let fontsPromise = null;

/** Load display fonts once; silently fall back to system serif/sans on failure. */
function ensureStandFonts() {
  if (typeof document === 'undefined') return Promise.resolve();
  if (fontsPromise) return fontsPromise;

  fontsPromise = new Promise((resolve) => {
    try {
      const href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Jost:wght@300;400;500;600&display=swap';
      if (!document.querySelector(`link[data-roomnexa-qr-fonts="1"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-roomnexa-qr-fonts', '1');
        document.head.appendChild(link);
      }
      const loaders = document.fonts
        ? [
          document.fonts.load('700 80px "Cormorant Garamond"'),
          document.fonts.load('600 40px "Jost"'),
          document.fonts.load('400 28px "Jost"'),
        ]
        : [];
      const timeout = new Promise((done) => setTimeout(done, 2500));
      Promise.race([Promise.all(loaders).catch(() => null), timeout]).then(() => resolve());
    } catch (err) {
      resolve();
    }
  });

  return fontsPromise;
}

function loadImage(src, { crossOrigin } = {}) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    try {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = crossOrigin;
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      img.onload = () => finish(img);
      img.onerror = () => finish(null);
      setTimeout(() => finish(null), 5000);
      img.src = src;
    } catch (err) {
      resolve(null);
    }
  });
}

function roundRectPath(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Measure text width with manual letter tracking applied. */
function trackedWidth(ctx, text, tracking) {
  if (!tracking) return ctx.measureText(text).width;
  const chars = Array.from(text);
  return chars.reduce((sum, char) => sum + ctx.measureText(char).width, 0)
    + tracking * Math.max(chars.length - 1, 0);
}

/** Draw centered text with optional manual letter tracking (canvas letterSpacing is not universal). */
function drawTrackedText(ctx, text, centerX, baselineY, tracking = 0) {
  const value = String(text ?? '');
  if (!value) return;
  if (!tracking) {
    ctx.textAlign = 'center';
    ctx.fillText(value, centerX, baselineY);
    return;
  }
  const chars = Array.from(value);
  const total = trackedWidth(ctx, value, tracking);
  let cursor = centerX - total / 2;
  ctx.textAlign = 'left';
  chars.forEach((char) => {
    ctx.fillText(char, cursor, baselineY);
    cursor += ctx.measureText(char).width + tracking;
  });
  ctx.textAlign = 'center';
}

/** Pick the largest font size (within range) that keeps text inside maxWidth. */
function fitFontSize(ctx, text, {
  maxWidth,
  maxSize,
  minSize,
  weight = '400',
  family = TEXT_FONT_STACK,
  tracking = 0,
}) {
  let size = maxSize;
  while (size > minSize) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (trackedWidth(ctx, text, tracking) <= maxWidth) break;
    size -= 2;
  }
  ctx.font = `${weight} ${size}px ${family}`;
  return size;
}

function wrapLines(ctx, text, maxWidth, maxLines = 2) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const trimmed = lines.slice(0, maxLines);
  trimmed[maxLines - 1] = `${trimmed[maxLines - 1].replace(/[\s.]+$/, '')}…`;
  return trimmed;
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'RN';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function drawBackground(ctx) {
  const gradient = ctx.createLinearGradient(0, 0, CANVAS_WIDTH * 0.35, CANVAS_HEIGHT);
  gradient.addColorStop(0, PALETTE.inkTop);
  gradient.addColorStop(0.55, '#0d2530');
  gradient.addColorStop(1, PALETTE.inkBottom);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  // Soft brand halo behind the QR panel (subtle atmosphere, not a glow effect).
  const halo = ctx.createRadialGradient(600, 1010, 60, 600, 1010, 640);
  halo.addColorStop(0, 'rgba(76, 225, 182, 0.16)');
  halo.addColorStop(1, 'rgba(76, 225, 182, 0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 380, CANVAS_WIDTH, 1200);

  // Fine paper texture: faint diagonal hairlines.
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  for (let x = -CANVAS_HEIGHT; x < CANVAS_WIDTH; x += 26) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + CANVAS_HEIGHT, CANVAS_HEIGHT);
    ctx.stroke();
  }
  ctx.restore();

  // Frame
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
  ctx.lineWidth = 3;
  roundRectPath(ctx, 44, 44, CANVAS_WIDTH - 88, CANVAS_HEIGHT - 88, 40);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(76, 225, 182, 0.28)';
  ctx.lineWidth = 1.5;
  roundRectPath(ctx, 62, 62, CANVAS_WIDTH - 124, CANVAS_HEIGHT - 124, 30);
  ctx.stroke();
}

function drawLogo(ctx, logoImg, hotelName) {
  const centerX = 600;
  const centerY = 206;
  const radius = 76;

  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius + 10, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(76, 225, 182, 0.45)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();

  if (logoImg) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.clip();
    const scale = Math.max((radius * 2) / logoImg.width, (radius * 2) / logoImg.height);
    const drawWidth = logoImg.width * scale;
    const drawHeight = logoImg.height * scale;
    ctx.drawImage(
      logoImg,
      centerX - drawWidth / 2,
      centerY - drawHeight / 2,
      drawWidth,
      drawHeight
    );
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fillStyle = PALETTE.accentSoft;
  ctx.fill();
  ctx.fillStyle = PALETTE.accent;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `600 64px ${DISPLAY_FONT_STACK}`;
  ctx.fillText(getInitials(hotelName), centerX, centerY + 4);
  ctx.restore();
  ctx.textBaseline = 'alphabetic';
}

/**
 * Compose the downloadable QR stand artwork.
 * Returns a PNG data URL, or null when composition is not possible.
 */
export async function buildQrStandArtwork({
  qrDataUrl,
  hotelName = 'RoomNexa Hotel',
  hotelLocation = '',
  logoUrl = '',
  targetLabel = '',
  targetCaption = '',
  ctaText = 'Scan to View Menu & Order Instantly',
  helperText = 'Open your camera, scan the code, and order in seconds',
  websiteText = '',
  contactText = '',
} = {}) {
  if (!qrDataUrl || typeof document === 'undefined') return null;

  await ensureStandFonts();

  const [qrImg, logoImg] = await Promise.all([
    loadImage(qrDataUrl),
    loadImage(logoUrl, { crossOrigin: 'anonymous' }),
  ]);
  if (!qrImg) return null;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.textBaseline = 'alphabetic';
  drawBackground(ctx);
  drawLogo(ctx, logoImg, hotelName);

  // Hotel name
  ctx.fillStyle = PALETTE.textPrimary;
  fitFontSize(ctx, hotelName, {
    maxWidth: 940,
    maxSize: 86,
    minSize: 44,
    weight: '700',
    family: DISPLAY_FONT_STACK,
  });
  drawTrackedText(ctx, hotelName, 600, 380, 1);

  // Location / property line
  if (hotelLocation) {
    ctx.fillStyle = PALETTE.textMuted;
    fitFontSize(ctx, hotelLocation.toUpperCase(), {
      maxWidth: 860,
      maxSize: 26,
      minSize: 18,
      weight: '400',
      family: TEXT_FONT_STACK,
      tracking: 6,
    });
    drawTrackedText(ctx, hotelLocation.toUpperCase(), 600, 424, 6);
  }

  // Hairline separator
  ctx.strokeStyle = 'rgba(76, 225, 182, 0.55)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(510, 464);
  ctx.lineTo(690, 464);
  ctx.stroke();

  // Table / Room identifier
  if (targetLabel) {
    const label = targetLabel.toUpperCase();
    const size = fitFontSize(ctx, label, {
      maxWidth: 700,
      maxSize: 46,
      minSize: 28,
      weight: '600',
      family: TEXT_FONT_STACK,
      tracking: 8,
    });
    const textWidth = trackedWidth(ctx, label, 8);
    const chipWidth = Math.min(textWidth + 96, 860);
    const chipHeight = size + 44;
    const chipX = 600 - chipWidth / 2;
    const chipY = 512;
    ctx.strokeStyle = 'rgba(76, 225, 182, 0.75)';
    ctx.lineWidth = 2.5;
    ctx.fillStyle = 'rgba(76, 225, 182, 0.10)';
    roundRectPath(ctx, chipX, chipY, chipWidth, chipHeight, chipHeight / 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#eafff8';
    ctx.font = `600 ${size}px ${TEXT_FONT_STACK}`;
    drawTrackedText(ctx, label, 600, chipY + chipHeight / 2 + size * 0.35, 8);

    if (targetCaption) {
      ctx.fillStyle = PALETTE.textMuted;
      fitFontSize(ctx, targetCaption, {
        maxWidth: 520,
        maxSize: 24,
        minSize: 16,
        weight: '300',
        family: TEXT_FONT_STACK,
        tracking: 3,
      });
      drawTrackedText(ctx, targetCaption, 600, chipY + chipHeight + 38, 3);
    }
  }

  // QR panel
  const panelSize = 700;
  const panelX = (CANVAS_WIDTH - panelSize) / 2;
  const panelY = 660;
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 18;
  ctx.fillStyle = PALETTE.paper;
  roundRectPath(ctx, panelX, panelY, panelSize, panelSize, 44);
  ctx.fill();
  ctx.restore();

  const qrSize = 596;
  ctx.drawImage(
    qrImg,
    panelX + (panelSize - qrSize) / 2,
    panelY + (panelSize - qrSize) / 2,
    qrSize,
    qrSize
  );

  // Corner registration marks around the panel
  ctx.strokeStyle = 'rgba(76, 225, 182, 0.7)';
  ctx.lineWidth = 4;
  const markLength = 46;
  const inset = 26;
  const corners = [
    [panelX - inset, panelY - inset, 1, 1],
    [panelX + panelSize + inset, panelY - inset, -1, 1],
    [panelX - inset, panelY + panelSize + inset, 1, -1],
    [panelX + panelSize + inset, panelY + panelSize + inset, -1, -1],
  ];
  corners.forEach(([cx, cy, dx, dy]) => {
    ctx.beginPath();
    ctx.moveTo(cx + dx * markLength, cy);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx, cy + dy * markLength);
    ctx.stroke();
  });

  // Call to action
  ctx.fillStyle = PALETTE.textPrimary;
  ctx.font = `500 46px ${TEXT_FONT_STACK}`;
  const ctaLines = wrapLines(ctx, ctaText, 900, 2);
  let ctaBaseline = 1478;
  ctaLines.forEach((line) => {
    ctx.textAlign = 'center';
    ctx.fillText(line, 600, ctaBaseline);
    ctaBaseline += 58;
  });

  if (helperText) {
    ctx.fillStyle = PALETTE.textMuted;
    ctx.font = `300 26px ${TEXT_FONT_STACK}`;
    const helperLines = wrapLines(ctx, helperText, 860, 1);
    ctx.fillText(helperLines[0] || '', 600, ctaBaseline + 4);
  }

  // Footer
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(220, 1620);
  ctx.lineTo(980, 1620);
  ctx.stroke();

  const contactBits = [websiteText, contactText].map((bit) => String(bit || '').trim()).filter(Boolean);
  if (contactBits.length) {
    const contactLine = contactBits.join('   ·   ');
    ctx.fillStyle = 'rgba(233, 246, 243, 0.78)';
    fitFontSize(ctx, contactLine, {
      maxWidth: 900,
      maxSize: 30,
      minSize: 18,
      weight: '400',
      family: TEXT_FONT_STACK,
      tracking: 1,
    });
    drawTrackedText(ctx, contactLine, 600, 1674, 1);
  }

  ctx.fillStyle = 'rgba(76, 225, 182, 0.85)';
  ctx.font = `500 22px ${TEXT_FONT_STACK}`;
  drawTrackedText(ctx, 'POWERED BY ROOMNEXA', 600, 1716, 7);

  try {
    return canvas.toDataURL('image/png');
  } catch (err) {
    return null;
  }
}

export default buildQrStandArtwork;
